import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FaGithub, FaLinkedin, FaFileAlt, FaLink, FaRegFileAlt, FaProjectDiagram } from "react-icons/fa";

export default function GetStarted() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const template = searchParams.get("template") || "student-dev";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    bio: "",
    profilePic: "",
    github: "",
    linkedin: "",
    resume: "",
    project1: { name: "", link: "", desc: "" },
    project2: { name: "", link: "", desc: "" },
    template,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("project")) {
      const [projKey, field] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [projKey]: {
          ...prev[projKey],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch("http://localhost:5000/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (result.success) alert("Form submitted!");
      navigate("/payment", { state: formData });
    } catch (err) {
      console.error("Submission error:", err);
      alert("Form submission failed.");
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white px-6 py-10 transition-all">
  <h2 className="text-4xl font-bold text-purple-700 dark:text-purple-400 text-center mb-6">
    Let's Build Your Brand
  </h2>

  <form
    onSubmit={handleSubmit}
    className="max-w-3xl mx-auto bg-gray-100 dark:bg-[#1c1c1e] p-6 rounded-xl shadow-md space-y-5"
  >
    <input name="template" type="hidden" value={template} />

    {[
      { label: "Name", name: "name" },
      { label: "Email", name: "email" },
      { label: "Bio", name: "bio" },
      { label: "Profile Picture URL", name: "profilePic" },
      { label: "GitHub Link", name: "github", icon: <FaGithub className="text-purple-400" /> },
      { label: "LinkedIn Link", name: "linkedin", icon: <FaLinkedin className="text-purple-400" /> },
      { label: "Resume Link", name: "resume", icon: <FaFileAlt className="text-purple-400" /> },
    ].map((field) => (
      <div key={field.name}>
        <label className="block mb-1 text-sm flex items-center gap-2">
          {field.icon} {field.label}
        </label>
        <input
          type="text"
          name={field.name}
          value={formData[field.name]}
          onChange={handleChange}
          className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-900 text-black dark:text-white rounded"
          required
        />
      </div>
    ))}

    <h3 className="text-xl text-purple-700 dark:text-purple-300 mt-6 mb-2">Projects</h3>

    {[1, 2].map((i) => (
      <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
        <div className="flex items-center gap-2">
          <FaProjectDiagram className="text-purple-400" />
          <input
            type="text"
            name={`project${i}.name`}
            placeholder={`Project ${i} Name`}
            value={formData[`project${i}`]?.name}
            onChange={handleChange}
            className="w-full bg-gray-200 dark:bg-gray-900 text-black dark:text-white px-4 py-2 rounded"
          />
        </div>

        <div className="flex items-center gap-2">
          <FaLink className="text-purple-400" />
          <input
            type="text"
            name={`project${i}.link`}
            placeholder={`Project ${i} Link`}
            value={formData[`project${i}`]?.link}
            onChange={handleChange}
            className="w-full bg-gray-200 dark:bg-gray-900 text-black dark:text-white px-4 py-2 rounded"
          />
        </div>

        <div className="flex items-center gap-2">
          <FaRegFileAlt className="text-purple-400" />
          <input
            type="text"
            name={`project${i}.desc`}
            placeholder={`Project ${i} Description`}
            value={formData[`project${i}`]?.desc}
            onChange={handleChange}
            className="w-full bg-gray-200 dark:bg-gray-900 text-black dark:text-white px-4 py-2 rounded"
          />
        </div>
      </div>
    ))}

    <button
      type="submit"
      className="mt-6 bg-purple-600 hover:bg-purple-700 px-6 py-3 rounded-full text-white w-full"
    >
      Submit & Build My Site
    </button>
  </form>

  {/* Live Preview Section */}
  <div className="mt-12 max-w-3xl mx-auto bg-gray-100 dark:bg-[#111] p-6 rounded-lg shadow-md transition-all">
    <h3 className="text-2xl font-semibold text-purple-700 dark:text-purple-400 mb-4">
      🔍 Preview
    </h3>
    <div className="flex items-center gap-4 mb-4">
      {formData.profilePic && (
        <img
          src={formData.profilePic}
          alt="Profile"
          className="w-20 h-20 rounded-full object-cover border border-purple-500"
        />
      )}
      <div>
        <p className="text-xl font-bold">{formData.name}</p>
        <p className="text-sm text-gray-700 dark:text-gray-400">{formData.email}</p>
      </div>
    </div>
    <p className="text-gray-800 dark:text-gray-300 mb-4">{formData.bio}</p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[formData.project1, formData.project2].map((proj, idx) => (
        proj?.name && (
          <div key={idx} className="bg-gray-200 dark:bg-[#222] p-4 rounded border border-purple-500">
            <h4 className="text-lg font-semibold text-purple-700 dark:text-purple-300">{proj.name}</h4>
            <p className="text-sm text-gray-700 dark:text-gray-400">{proj.desc}</p>
            {proj.link && (
              <a
                href={proj.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-purple-600 dark:text-purple-400 underline mt-2 inline-block"
              >
                Visit Project
              </a>
            )}
          </div>
        )
      ))}
    </div>
  </div>
</div>

  );
}
